<template>
  <page-loader>
    <v-container class="pt-0">
      <v-row>
        <v-col class="d-flex align-center">
          <v-breadcrumbs :items="breadcrumbs" class="px-0" />
          <v-spacer />
          <v-tooltip max-width="300" bottom>
            <template #activator="{ on }">
              <base-chip v-show="hasChanges" color="blue" text="Changes Detected" icon="info" dark small v-on="on">
              </base-chip>
            </template>
            <span>Click the FAB in the bottom right then click "Save Backlink" to keep your changes!</span>
          </v-tooltip>
        </v-col>
      </v-row>
      <v-divider />
    </v-container>
    <v-container>
      <v-row>
        <v-col v-if="categorisedSchema">
          <v-row v-for="(fields, category) in categorisedSchemaExcludeSite.general" :key="category">
            <v-col>
              <v-card class="mb-4" outlined>
                <v-card-subtitle class="px-3 py-3 mb-2 grey lighten-4">
                  Site
                </v-card-subtitle>
                <v-col>
                  <select-list
                    v-model="updatedBacklinkEntry.target_site_id"
                    v-bind="categorisedSchemaSiteOnly.target_site_id.props"
                    :items="sites"
                    persistent-hint
                    class="mb-6"
                  />
                  <v-combobox
                    v-model="updatedBacklinkEntry.campaign_name"
                    v-bind="categorisedSchemaSiteOnly.campaign_name.props"
                    :items="campaigns"
                    class="mb-6"
                    color="primary"
                    width="auto"
                    dense
                    outlined
                    persistent-hint
                  />
                  <text-input
                    v-model="updatedBacklinkEntry.target_url"
                    v-bind="categorisedSchemaSiteOnly.target_url.props"
                    :prefix="`${getSiteNameById(updatedBacklinkEntry.target_site_id) || ''}/`"
                    persistent-hint
                    class="mb-6"
                  />
                  <select-list
                    v-model="updatedBacklinkEntry.language_id"
                    label="Site Language"
                    :items="filteredLanguages"
                    persistent-hint
                    class="mb-6"
                  />
                  <select-list
                    v-model="updatedBacklinkEntry.coverage_type"
                    v-bind="categorisedSchemaSiteOnly.coverage_type.props"
                    :items="getItemsForOption('coverageTypes')"
                    class="mb-6"
                  />
                  <text-input
                    v-model="updatedBacklinkEntry.referring_domain"
                    v-bind="categorisedSchemaSiteOnly.referring_domain.props"
                    persistent-hint
                    class="mb-6"
                  />
                  <text-input
                    v-model.trim="updatedBacklinkEntry.referring_url"
                    v-bind="categorisedSchemaSiteOnly.referring_url.props"
                    persistent-hint
                    class="mb-2"
                  />
                  <v-row class="mt-0">
                    <v-col>
                      <text-input
                        v-model="updatedBacklinkEntry.domain_rating"
                        v-bind="categorisedSchemaSiteOnly.domain_rating.props"
                        type="number"
                      />
                    </v-col>
                    <v-col>
                      <text-input
                        v-model="updatedBacklinkEntry.trust_flow"
                        v-bind="categorisedSchemaSiteOnly.trust_flow.props"
                        type="number"
                      />
                    </v-col>
                    <v-col>
                      <text-input
                        v-model="updatedBacklinkEntry.estimated_traffic"
                        v-bind="categorisedSchemaSiteOnly.estimated_traffic.props"
                        type="number"
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </v-card>
              <div>
                <category-options
                  :category="category"
                  :data="updatedBacklinkEntry"
                  :fields="fields"
                  :sites="sites"
                  :users="users"
                  :is-disabled="!isPermittedTo('update')"
                  :is-loading="isLoading"
                  :show-category="false"
                  :group-cols="6"
                  @input="onInput"
                />
              </div>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>

    <fab-actions
      :can-delete="isPermittedTo('delete')"
      :can-edit="isPermittedTo('update')"
      :has-changes="hasChanges"
      context="Backlink"
      @delete="showDeleteModal = true"
      @reset="showResetModal = true"
      @save="showSaveModal = true"
    />

    <generic-modal
      v-model="showSaveModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Save Backlink"
      persistent
      @modal-confirm="onSaveConfirm"
      @modal-cancel="showSaveModal = false"
    >
      <span>Are you sure you want to save these changes?</span>
    </generic-modal>

    <reset-modal
      v-model="showResetModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      category="Backlink entry"
      @modal-confirm="onResetConfirm"
    />

    <generic-modal
      v-model="showDeleteModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Delete Backlink"
      confirm-color="error"
      confirm-text="Delete"
      @modal-confirm="onDeleteConfirm"
    >
      <div>
        Are you sure you want to <span class="red--text font-weight-bold">DELETE</span> this backlink entry? This cannot
        be undone.
      </div>
    </generic-modal>

    <exit-modal
      :is-disabled="isLoading"
      :is-loading="isLoading"
      :value="showExitModal"
      @modal-confirm="confirmExitBeforeSave"
      @modal-cancel="cancelExitBeforeSave"
    />
  </page-loader>
</template>

<script>
// Services
import { backlinks, languageSites, sites, users } from '@/services/offers'
// Helpers
import { mapGetters, mapMutations, mapState } from 'vuex'
import { clone, cloneDeep } from 'lodash-es'
// Mixins
import PermissionsHandler from '@/mixins/permissions-handler'
import DataModification from '@/mixins/data-modification'
// Components
import BaseChip from '@/components/chips/base-chip'
import CategoryOptions from '@/components/category-options'
import ExitModal from '@/components/modals/exit-modal'
import FabActions from '@/components/fab-actions'
import GenericModal from '@/components/modals/generic-modal'
import ResetModal from '@/components/modals/reset-modal'
import PageLoader from '@/components/page-loader'
import SelectList from '@/components/forms/select-list'
import TextInput from '@/components/forms/text-input'

export default {
  name: 'LinkTrackingEdit',
  components: {
    BaseChip,
    CategoryOptions,
    ExitModal,
    FabActions,
    GenericModal,
    ResetModal,
    PageLoader,
    SelectList,
    TextInput,
  },
  mixins: [DataModification, PermissionsHandler],
  data: () => ({
    originalBacklinkEntry: {},
    updatedBacklinkEntry: {},
    isLoading: false,
    languages: [],
    campaigns: [],
    showDeleteModal: false,
    showSaveModal: false,
    showResetModal: false,
    sites: [],
    tab: 0,
    users: [],
  }),
  computed: {
    ...mapGetters('options', ['getItemsForOption']),
    ...mapState('backlinks', ['schema', 'parsedSchema', 'categorisedSchema']),
    breadcrumbs() {
      return [
        { text: 'Backlink Tracking', to: { name: 'backlink-tracking' }, exact: true },
        { text: this.originalBacklinkEntry.id ? `#${this.originalBacklinkEntry.id}` : 'loading', disabled: true },
      ]
    },
    categorisedSchemaExcludeSite() {
      try {
        if (this.categorisedSchema?.general?.['General Information']?.Site) {
          const clonedCategorisedSchema = cloneDeep(this.categorisedSchema)
          delete clonedCategorisedSchema.general['General Information'].Site
          return clonedCategorisedSchema
        }

        return this.categorisedSchema
      } catch (e) {
        return this.categorisedSchema
      }
    },
    categorisedSchemaSiteOnly() {
      try {
        if (this.categorisedSchema?.general?.['General Information']?.Site) {
          return Object.values(this.categorisedSchema.general['General Information'].Site).reduce((acc, obj) => {
            acc[obj.model] = obj
            return acc
          }, {})
        }

        return this.categorisedSchema
      } catch (e) {
        return this.categorisedSchema
      }
    },
    filteredLanguages() {
      return this.languages.filter(({ site_id }) => site_id === this.updatedBacklinkEntry.target_site_id)
    },
    hasChanges() {
      return this.hasDataChanged(this.originalBacklinkEntry, this.updatedBacklinkEntry)
    },
  },
  async created() {
    await this.getLink()
    this.getCampaigns()
    this.getSites()
    this.getLanguageSites()
    this.getUsers()
    this.setBeforeUnloadListener(this.onBeforeUnload)
    this.setPageLoading(false)
  },
  beforeRouteLeave(to, from, next) {
    if (!this.hasChanges) return next()
    this.handleExitBeforeSave(next)
  },
  beforeDestroy() {
    this.removeBeforeUnloadListener(this.onBeforeUnload)
  },
  methods: {
    ...mapMutations('general', ['setPageLoading']),
    async deleteLink() {
      this.isLoading = true
      try {
        await backlinks.deleteBacklink(this.originalBacklinkEntry.id)
        this.$notify({
          type: 'success',
          text: 'Successfully deleted backlink entry',
        })
        return true
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to delete backlink entry',
        })
      }
      this.isLoading = false
      return false
    },
    async getLink() {
      this.isLoading = true
      try {
        this.originalBacklinkEntry = await backlinks.getBacklink(this.$route.params.id)
        this.updatedBacklinkEntry = clone(this.originalBacklinkEntry)
      } catch (e) {
        this.$notify({
          type: 'error',
          text: `Unable to retrieve data for backlink with ID: ${this.$route.params.id}`,
        })
        this.$router.push({ name: 'reports.backlink-tracking' })
      }
      this.isLoading = false
    },
    getSiteNameById(id) {
      const foundSite = this.sites.find(({ value }) => value === id)
      return foundSite ? foundSite.key : null
    },
    async getLanguageSites() {
      this.isLoading = true
      try {
        const response = await languageSites.getAll()
        this.languages = response.map(({ site_id, language: { name }, language_id }) => {
          return { key: name, value: language_id, site_id }
        })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch available languages',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
    async getSites() {
      this.isLoading = true
      try {
        const { data } = await sites.getAllSites({ filters: { type: 'organic' } })
        this.sites = data.map(({ name, id }) => ({ key: name, value: id }))
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve sites options',
        })
      }
      this.isLoading = false
    },
    async getUsers() {
      this.isLoading = true
      try {
        const { data } = await users.getFilteredUsers()
        this.users = data.map(({ name, id }) => ({ key: name, value: id }))
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve owners options',
        })
      }
      this.isLoading = false
    },
    async getCampaigns() {
      this.isLoading = true
      try {
        const { data } = await backlinks.getCampaigns()
        this.campaigns = data
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve campaigns',
        })
      }
      this.isLoading = false
    },
    onBeforeUnload(event) {
      if (!this.hasChanges) return
      this.confirmUnload(event)
    },
    async onDeleteConfirm() {
      const deleted = await this.deleteLink()
      if (deleted) {
        this.$router.push({ name: 'reports.backlink-tracking' })
      }
    },
    async onResetConfirm() {
      this.updatedBacklinkEntry = clone(this.originalBacklinkEntry)
      this.showResetModal = false
    },
    async onSaveConfirm() {
      await this.updateLink()
      this.showSaveModal = false
    },
    onInput({ context, model, value }) {
      if (context) {
        if (this.updatedBacklinkEntry[model]) {
          this.$set(this.updatedBacklinkEntry[model], context, value)
        } else {
          this.$set(this.updatedBacklinkEntry, model, { [context]: value })
        }
      } else this.$set(this.updatedBacklinkEntry, model, value)
    },
    async updateLink() {
      this.isLoading = true
      try {
        await backlinks.updateBacklink(this.$route.params.id, this.updatedBacklinkEntry)
        this.getLink()
        this.$notify({
          type: 'success',
          text: 'Successfully saved backlink entry',
        })
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to save backlink entry',
        })
      }
      this.isLoading = false
    },
  },
}
</script>
